import { useQuery } from 'react-query';

import { publicEnv } from '~/features/environment/public';
import { useUser } from '~/features/user/hooks/use-user';
import { useI18n } from '~/hooks/use-i18n';
import { queryUdacityService } from '~/utils/query-udacity-service';

import { certificateV2Schema, fetchCertificateV2ByLearningPlanSubscriptionIdQuery } from '../model';

/**
 * Fetches localized certificate V2 for unauthenticated users based on i18n language.
 */
export function useLocalizedCertificateV2({
  subscriptionId,
  initialLanguage,
}: {
  subscriptionId: string;
  initialLanguage: string;
}) {
  const { i18n } = useI18n();
  const { isUserAuthenticated } = useUser();
  const currentLanguage = i18n.language;

  // Only fetch if unauthenticated AND language differs from server-rendered version
  const shouldFetchLocalizedCertificate = Boolean(
    !isUserAuthenticated && currentLanguage && currentLanguage !== initialLanguage
  );

  const certificateQuery = useQuery(
    ['certificateV2', subscriptionId, currentLanguage],
    () => {
      return queryUdacityService({
        url: publicEnv.NEXT_PUBLIC_EXPERIENCE_URL + '/public',
        zodSchema: certificateV2Schema,
        shouldThrowOnGraphqlError: true,
        body: JSON.stringify({
          query: fetchCertificateV2ByLearningPlanSubscriptionIdQuery,
          variables: { subscriptionId },
        }),
        preferredLanguage: currentLanguage,
      });
    },
    {
      enabled: shouldFetchLocalizedCertificate,
      retry: false,
    }
  );

  return {
    certificate: certificateQuery.data,
    isLoading: certificateQuery.isLoading,
    isFetched: certificateQuery.isFetched,
  };
}
